import { CommonModule } from '@angular/common';
import { Component, forwardRef, inject, Input, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormArray,
  FormBuilder,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { distinctUntilChanged, map, Subject, takeUntil } from 'rxjs';

import { ControlErrorComponent } from '../../../sdk/forms/error';
import { TimeRange, TimerangeComponent } from '../timerange';
import { DAY_OF_WEEK_TRANSLATIONS, DEFAULT_TIME_INTERVAL, WEEKDAY } from '../utils';
import { createOverlapValidator } from './overlap.validator';

export type TimeRangeSchedule = Record<WEEKDAY, TimeRange[]>;

export function isScheduleEqual(a: TimeRangeSchedule, b: TimeRangeSchedule): boolean {
  if (a === b) return true;
  return JSON.stringify(a) === JSON.stringify(b);
}

@Component({
  selector: 'rms-timerange-schedule',
  imports: [
    ReactiveFormsModule,
    MatIconModule,
    TimerangeComponent,
    MatDivider,
    TranslateModule,
    ControlErrorComponent,
    CommonModule,
    MatButton,
  ],
  templateUrl: './timerange-schedule.component.html',
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => TimerangeScheduleComponent), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => TimerangeScheduleComponent), multi: true },
  ],
})
export class TimerangeScheduleComponent implements ControlValueAccessor, Validator, OnInit, OnDestroy {
  @Input() public defaultTimeRange: TimeRange | null = null;
  @Input() public interval = DEFAULT_TIME_INTERVAL;
  @Input() public allowIntersections = true;
  @Input() public variant: 'sm' | 'default' = 'default';
  protected WEEKDAYS = Object.values(WEEKDAY);
  protected dayOfWeekLabel = DAY_OF_WEEK_TRANSLATIONS;

  private readonly fb = inject(FormBuilder);
  private readonly destroy$ = new Subject<void>();

  protected form = this.fb.group(
    this.WEEKDAYS.reduce((acc: Record<string, FormArray>, day) => {
      acc[day] = this.fb.array([], [createOverlapValidator(() => this.allowIntersections)]);

      return acc;
    }, {})
  );

  private onChange: (value: TimeRangeSchedule) => void = () => {};
  public onTouched: () => void = () => {};

  ngOnInit(): void {
    this.form.valueChanges
      .pipe(
        map((raw) => raw as TimeRangeSchedule),
        distinctUntilChanged(isScheduleEqual),
        takeUntil(this.destroy$),
      )
      .subscribe((value) => {
        this.onTouched();
        this.onChange(value);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public getCombinedErrors(day: string, index: number): ValidationErrors | null {
    const isOverlapping = this.getOverlappingIndices(day).includes(index);
    if (isOverlapping) {
      return { overlap: 'validation.intervals.overlapping' };
    }

    return null;
  }

  writeValue(value: TimeRangeSchedule | null): void {
    if (!value) {
      return;
    }

    Object.keys(value).forEach((day) => {
      const control = this.form.get(day) as FormArray;
      control.clear();
      value[day as WEEKDAY].forEach((timeRange: TimeRange) => control.push(this.fb.control(timeRange)));
    });
  }

  registerOnChange(fn: (value: TimeRangeSchedule) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.form.disable() : this.form.enable();
  }

  validate(_control: AbstractControl): ValidationErrors | null {
    for (const day of this.WEEKDAYS) {
      const arr = this.form.get(day) as FormArray;
      if (arr.errors?.['overlaps']) {
        return { overlap: true };
      }
    }
    return null;
  }

  public getOverlappingIndices(day: string): number[] {
    const formArray = this.getTimeRangesControl(day);

    return formArray.getError('overlaps') || [];
  }

  protected getTimeRangesControl(day: string): FormArray<FormControl> {
    return this.form.get(day) as FormArray<FormControl>;
  }

  protected addTimeRange(day: string): void {
    this.getTimeRangesControl(day).push(this.fb.control(this.defaultTimeRange));
  }

  protected removeTimeRange(day: string, index: number): void {
    this.getTimeRangesControl(day).removeAt(index);
  }
}
